
import { Client } from "@/models/Client";
import { FormData } from "@/components/clients/utils/clientFormSchema";
import { UseFormReturn } from "react-hook-form";
import { createClientData } from "./formDataUtils";
import { validateAndNavigateToFirstError } from "./formValidation";
import { resetClientsListStateToDefault } from "@/utils/localStorage";

interface FormHandlersOptions {
  isEditMode: boolean;
  initialData: Client | undefined;
  createClientMutation: any;
  updateClientMutation: any;
  updatePaymentStatusMutation: any;
  handleSuccess: () => void;
  setActiveTab: (tab: string) => void;
  form: UseFormReturn<FormData>;
  navigateToClient?: (clientId: string) => void;
  organizationId?: string;
}

export const createFormHandlers = (options: FormHandlersOptions) => {
  const { 
    isEditMode, 
    initialData, 
    createClientMutation, 
    updateClientMutation, 
    updatePaymentStatusMutation, 
    handleSuccess, 
    setActiveTab, 
    form,
    navigateToClient,
    organizationId
  } = options;

  const handleDetailsSubmit = (data: FormData): void => {
    // Handle async operations internally
    (async () => {
      console.log('Submitting client details only:', data);
      console.log('Current active tab:', form.watch());
      
      // Validate and navigate to first error if any
      const validation = await validateAndNavigateToFirstError(form, setActiveTab);
      console.log('Validation result:', validation);
      if (!validation.isValid) {
        console.log('Validation failed, staying on current form');
        return;
      }
      
      // When updating details, don't include subscriptions
      const clientData = createClientData(data, false);

      if (isEditMode && initialData) {
        // Check if payment status changed
        const paymentStatusChanged = data.paymentStatus !== initialData.paymentStatus;
        
        // Update client data first
        await updateClientMutation.mutateAsync({
          id: initialData.id,
          clientData
        });

        // If payment status changed, update it separately
        if (paymentStatusChanged && data.paymentStatus) {
          await updatePaymentStatusMutation.mutateAsync({
            clientId: initialData.id,
            paymentStatus: data.paymentStatus
          });
        }

        handleSuccess();
      }
    })().catch(console.error);
  };

  const handleSubscriptionsSubmit = (data: FormData): void => {
    // Handle async operations internally
    (async () => {
      console.log('=== SUBSCRIPTIONS SUBMIT STARTED ===');
      console.log('Submitting subscriptions only:', data);
      console.log('Subscription data from form:', data.subscriptions);
      console.log('Current form errors:', form.formState.errors);
      
      // Validate and navigate to first error if any
      const validation = await validateAndNavigateToFirstError(form, setActiveTab);
      console.log('Subscriptions validation result:', validation);
      if (!validation.isValid) {
        console.log('Validation failed, navigating to error tab');
        return;
      }
      
      // When updating subscriptions, include them
      const clientData = createClientData(data, true);
      console.log('Client data created for subscriptions update:', clientData);
      console.log('Subscriptions in clientData:', clientData.subscriptions);

      if (isEditMode && initialData) {
        console.log('Updating client subscriptions for ID:', initialData.id);
        updateClientMutation.mutate({
          id: initialData.id,
          clientData
        }, {
          onSuccess: handleSuccess
        });
      }
    })().catch(console.error);
  };

  const handleFullSubmit = (data: FormData): void => {
    // Handle async operations internally
    (async () => {
      console.log('=== FULL SUBMIT STARTED ===');
      console.log('Form submitted with data:', data);
      console.log('Current form errors before validation:', form.formState.errors);
      console.log('Current active tab would be affected by setActiveTab');
      
      // Force re-validation of all fields
      console.log('Triggering validation...');
      const allFieldsValid = await form.trigger();
      console.log('All fields valid after trigger:', allFieldsValid);
      console.log('Form errors after trigger:', form.formState.errors);
      
      // Validate and navigate to first error if any
      const validation = await validateAndNavigateToFirstError(form, setActiveTab);
      console.log('Full submit validation result:', validation);
      if (!validation.isValid) {
        console.log('Validation failed, should navigate to error tab:', validation.errorTab);
        console.log('Error field:', validation.errorField);
        return;
      }
      
      console.log('Validation passed, proceeding with submission');
      const clientData = createClientData(data, true);
      console.log('Client data to submit:', clientData);

      if (isEditMode && initialData) {
        updateClientMutation.mutate({
          id: initialData.id,
          clientData
        }, {
          onSuccess: handleSuccess
        });
      } else {
        createClientMutation.mutate(clientData, {
          onSuccess: (createdClient: Client) => {
            // Reset client list state to default so new client appears at top
            resetClientsListStateToDefault(organizationId);
            handleSuccess();
            // Navigate to client details if we have a navigation function and the client was created
            if (navigateToClient && createdClient) {
              navigateToClient(createdClient.id);
            }
          }
        });
      }
    })().catch(console.error);
  };

  return {
    handleDetailsSubmit,
    handleSubscriptionsSubmit,
    handleFullSubmit
  };
};
